import type { LinkBudgetResult } from '@freedom/rf-engine';
import type { DesignSite } from '@freedom/shared';

export const DEFAULT_PTP_DOWNLINK_SHARE = 0.5;

export interface DirectionalCapacityBudgets {
  forward: Pick<LinkBudgetResult, 'usableThroughputMbps'>;
  reverse: Pick<LinkBudgetResult, 'usableThroughputMbps'>;
}

export interface PtPCapacityBreakdown {
  downlinkMbps: number;
  uplinkMbps: number;
  totalMbps: number;
  maximumMbps: number | null;
  downlinkShare: number;
}

function usableCapacity(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function selectedChannelMaximum(site: DesignSite): number | null {
  const channelProfile = site.radioPerformanceProfiles?.find((profile) => profile.channelWidthMHz === site.channelWidthMHz);
  return channelProfile?.entries.reduce<number | null>((maximum, entry) => {
    if (!Number.isFinite(entry.usableThroughputMbps) || entry.usableThroughputMbps <= 0) return maximum;
    return maximum === null ? entry.usableThroughputMbps : Math.max(maximum, entry.usableThroughputMbps);
  }, null) ?? null;
}

/**
 * Directional link budgets describe one-way potential at 100% airtime. PtP
 * radios share the channel, so UISP's default 50/50 TDD view assigns half of
 * the airtime to each direction and then sums both allocated rates.
 */
export function calculatePtPCapacity(
  budgets: DirectionalCapacityBudgets,
  endpoints: readonly [DesignSite, DesignSite],
  downlinkShare = DEFAULT_PTP_DOWNLINK_SHARE,
): PtPCapacityBreakdown {
  const normalizedDownlinkShare = Number.isFinite(downlinkShare) ? Math.max(0, Math.min(1, downlinkShare)) : DEFAULT_PTP_DOWNLINK_SHARE;
  const rawDownlink = usableCapacity(budgets.forward.usableThroughputMbps) * normalizedDownlinkShare;
  const rawUplink = usableCapacity(budgets.reverse.usableThroughputMbps) * (1 - normalizedDownlinkShare);
  const endpointMaximums = endpoints.map(selectedChannelMaximum);
  const maximumMbps = endpointMaximums.every((value): value is number => value !== null)
    && Math.abs(endpointMaximums[0]! - endpointMaximums[1]!) < 1e-6
    ? endpointMaximums[0]!
    : null;

  return {
    downlinkMbps: rawDownlink,
    uplinkMbps: rawUplink,
    totalMbps: rawDownlink + rawUplink,
    maximumMbps,
    downlinkShare: normalizedDownlinkShare,
  };
}

export function formatCapacityMbps(value: number): string {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
  const roundedInteger = Math.round(safeValue);
  if (safeValue >= 100 || Math.abs(safeValue - roundedInteger) < 0.05) return String(roundedInteger);
  return (Math.round(safeValue * 10) / 10).toFixed(1).replace('.', ',');
}
